import { useMemo } from 'react';

import isNil from 'lodash/isNil';

import { dateHelpers, formatHelpers } from '@nocowanie/core';

import { AccommodationTileProps } from '@app/components/molecules/accommodation-tile';
import { ProvisionType } from '@app/models/api/api-types';
import { AccommodationTileModel } from '@app/models/elastic-search';

export const useAccommodationRatePlanFeatures = (
    t: AccommodationTileProps['t'],
    rate?: AccommodationTileModel['rate'],
) => {
    const features = useMemo(() => {
        if (isNil(rate)) {
            return null;
        }

        const { cancellationPolicy, prepayment, provision } = rate;
        const isCancellationFree = cancellationPolicy?.freeCancellation;
        const isCancellationNonRefundable = cancellationPolicy?.notRefundable;
        const features = [];

        if (provision && provision.type !== ProvisionType.NoMeal) {
            features.push({
                key: 'provision',
                dataTestId: 'acc-tile-provision',
                color: 'green.solid',
                content: t('ratePlan.provisionIncluded', {
                    provision: provision.name,
                }),
            });
        }

        if (isCancellationFree || isCancellationNonRefundable) {
            const cancellationText = () => {
                if (isCancellationNonRefundable) {
                    return t('ratePlan.cancellationPolicies.nonRefundable');
                } else if (isCancellationFree) {
                    if (cancellationPolicy.cancellationDateTo) {
                        return t('ratePlan.cancellationPolicies.freeUntil', {
                            date: dateHelpers
                                .format(new Date(cancellationPolicy.cancellationDateTo), 'd MMM')
                                .toLowerCase(),
                        });
                    }

                    return t('ratePlan.cancellationPolicies.free');
                }
                return null;
            };

            const text = cancellationText();

            if (text) {
                features.push({
                    key: 'cancellation',
                    dataTestId: 'acc-tile-cancellation',
                    color: isCancellationFree ? 'green.solid' : undefined,
                    content: text,
                });
            }
        }

        if (prepayment) {
            const noPrepayment = prepayment.rate === 0;
            const prepaymentText = () => {
                if (noPrepayment) {
                    return t('ratePlan.prepayment.noPrepayment');
                }
                if (prepayment.fullPrepayment) {
                    return t('ratePlan.prepayment.fullPrepayment');
                }
                return t('ratePlan.prepayment.partialPrepayment', {
                    prepayment: formatHelpers.price(
                        prepayment.convertedRate,
                        rate.convertedCurrency,
                    ),
                });
            };

            features.push({
                key: 'prepayment',
                dataTestId: 'acc-tile-prepayment',
                content: prepaymentText(),
                color: noPrepayment ? 'green.solid' : undefined,
            });
        }

        return features;
    }, [rate, t]);

    return features;
};
